// src/components/EntrepreneurDetailsPopup.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
} from 'firebase/firestore';
import { db } from '../firebaseConfig';
import H3 from './H3/H3';
import AuthorDetails from './AuthorDetails';
import Button from './Button/Button';
import distanceIcon from '../assets/ico-km-distancia.svg';
import closeBtnImage from '../assets/btn-close.png';
import heartIconOver from '../assets/ico-coracao-over.svg';
import heartIcon from '../assets/ico-favorito.svg';
import messageIcon from '../assets/ico-message.svg';
import {
  normalizeUrl,
  isValidUrl,
  formatUrlForDisplay,
  extractInstagramHandle,
  normalizeInstagramUrl,
  formatPhoneForTel,
  formatPhoneForDisplay,
} from '../utils/contactFormatters';

const categoryNamesWithAccent = {
  moradia: 'Moradia',
  alimentacao: 'Alimentação',
  trocas: 'Trocas',
  transporte: 'Transporte',
  documentacao: 'Documentação',
  emprego: 'Emprego',
  comunicacao: 'Comunicação',
  treinamento: 'Treinamento',
  saude: 'Saúde',
  outros: 'Outros',
};

const EntrepreneurDetailsPopup = ({ entrepreneur, onClose, authorInfo, isFavorite, onToggleFavorite }) => {
  const { category, businessCity, businessState, businessAddress, businessHours, website, instagram, businessPhone } = entrepreneur;
  const categoryName = category ? categoryNamesWithAccent[category] : '';

  const websiteHref = isValidUrl(website) ? normalizeUrl(website) : '';
  const websiteLabel = websiteHref ? formatUrlForDisplay(websiteHref) : '';
  const instagramHandleRaw = extractInstagramHandle(instagram);
  const instagramHref = instagramHandleRaw ? normalizeInstagramUrl(instagram) : '';
  const instagramLabel = instagramHandleRaw ? `@${instagramHandleRaw}` : '';
  const phoneHref = formatPhoneForTel(businessPhone);
  const phoneLabel = formatPhoneForDisplay(businessPhone);

  const navigate = useNavigate();
  const auth = getAuth();
  const currentUser = auth.currentUser;
  const isOwnEntrepreneur = currentUser && entrepreneur.userId === currentUser.uid;

  const handleResponderClick = async () => {
    if (!currentUser || !entrepreneur.userId) return;

    const conversationsQuery = query(
      collection(db, 'conversations'),
      where('users', 'array-contains', currentUser.uid),
    );
    const conversationsSnapshot = await getDocs(conversationsQuery);
    let existingConversationId = null;
    conversationsSnapshot.forEach((docSnap) => {
      if (docSnap.data().users.includes(entrepreneur.userId)) {
        existingConversationId = docSnap.id;
      }
    });

    const initialMessageData = {
      sender: currentUser.uid,
      text: `Olá! Vi o perfil da empresa ${entrepreneur.companyName || ''} na Vitrine de Empreendedores e gostaria de saber mais.`.trim(),
      createdAt: new Date(),
    };

    try {
      if (existingConversationId) {
        await addDoc(
          collection(db, `conversations/${existingConversationId}/messages`),
          initialMessageData,
        );
        navigate('/central-de-mensagens', {
          state: { selectedConversationId: existingConversationId },
        });
        return;
      }

      const newConversationRef = await addDoc(collection(db, 'conversations'), {
        users: [currentUser.uid, entrepreneur.userId],
        createdAt: new Date(),
      });
      await addDoc(
        collection(db, `conversations/${newConversationRef.id}/messages`),
        initialMessageData,
      );
      navigate('/central-de-mensagens', {
        state: { selectedConversationId: newConversationRef.id },
      });
    } catch (error) {
      console.error('Erro ao iniciar conversa com empreendedor:', error);
    }
  };

  return (
    <>
      {/* Desktop View */}
      <div className="hidden md:flex fixed inset-0 z-[9999] bg-yellow-300 justify-center items-center">
        <div className="relative" style={{ width: '795px', maxHeight: '90vh' }}>
          <button className="absolute -top-3.5 -right-3.5 w-8 h-8 bg-transparent border-none cursor-pointer p-0 z-20" onClick={onClose}>
            <img src={closeBtnImage} alt="Close" />
          </button>
          <div className="bg-white rounded-xl p-10 flex flex-col relative overflow-y-auto overflow-x-hidden w-full" style={{ minHeight: '480px', maxHeight: '90vh' }}>

          {/* First Row for Favorite Button */}
          <div className="flex-none w-full mb-5">
            <button
              className="absolute top-10 right-10 w-6 h-6"
              onClick={onToggleFavorite}
            >
              <img
                src={isFavorite ? heartIconOver : heartIcon}
                alt="Favoritar"
                className="w-6 h-6"
              />
            </button>
          </div>

          {/* Second Row for Content */}
          <div className="flex w-full flex-grow">
            <div className="w-3/5 pr-2 min-w-0">
              <div className="flex flex-col justify-between flex-grow">
                {category && (
                  <div className="font-sans text-sm font-bold text-[#EE7911] mb-2">{categoryName}</div>
                )}
                <div className="flex justify-between items-center">
                  <H3 text={entrepreneur.companyName} textColor="purple" />
                  {businessCity && businessState && (
                    <div className="flex items-center -mt-6 pr-8">
                      <img src={distanceIcon} alt="Distance" className="w-4 h-4 mr-1" />
                      <p className="text-sm text-gray-500">{businessCity} - {businessState}</p>
                    </div>
                  )}
                </div>
                <p className="text-base text-gray-600 -mt-5 mb-4 break-words">{entrepreneur.aboutBusiness}</p>
                <p className="text-base text-gray-600 mb-2 break-words">Endereço: {businessAddress}</p>
                <p className="text-base text-gray-600 mb-5 break-words">Horários: {businessHours}</p>
                {/* Links */}
                {websiteHref && (
                  <div className="flex items-center mb-3">
                    <img src="/img/ico-url-perfil.svg" alt="" className="w-5 h-5 mr-1.5" />
                    <a href={websiteHref} target="_blank" rel="noopener noreferrer" className="text-[#4663AB] font-sans font-bold text-sm break-all">
                      {websiteLabel}
                    </a>
                  </div>
                )}
                {instagramHref && (
                  <div className="flex items-center mb-3">
                    <img src="/img/ico-instagram-perfil.svg" alt="" className="w-5 h-5 mr-1.5" />
                    <a href={instagramHref} target="_blank" rel="noopener noreferrer" className="text-[#4663AB] font-sans font-bold text-sm break-all">
                      {instagramLabel}
                    </a>
                  </div>
                )}
                {phoneHref && (
                  <div className="flex items-center mb-4">
                    <img src="/img/ico-telefone-perfil.svg" alt="" className="w-5 h-5 mr-1.5" />
                    <a href={`tel:${phoneHref}`} className="text-[#4663AB] font-sans font-bold text-sm">
                      {phoneLabel}
                    </a>
                  </div>
                )}
                {!isOwnEntrepreneur && currentUser && (
                  <div className="mt-2">
                    <Button text="Responder" variant="icon" icon={messageIcon} onClick={handleResponderClick} />
                  </div>
                )}
              </div>
            </div>
            <div className="w-2/5 mt-5">
              {authorInfo && <AuthorDetails authorInfo={authorInfo} title="Perfil do empreendedor" />}
            </div>
          </div>
          </div>
        </div>
      </div>

      {/* Mobile View */}
      <div className="md:hidden fixed inset-0 z-[9999] bg-yellow-300 flex justify-center items-center">
        <div className="bg-white rounded-lg p-8 flex flex-col relative w-5/6 h-[90%]">
          <button className="absolute -top-3.5 -right-3.5 w-8 h-8 bg-transparent border-none cursor-pointer p-0" onClick={onClose}>
            <img src={closeBtnImage} alt="Close" />
          </button>

          {/* First Row for Favorite Button */}
          <div className="flex-none w-full mb-5">
            <button
              className="absolute top-10 right-10 w-6 h-6"
              onClick={onToggleFavorite}
            >
              <img
                src={isFavorite ? heartIconOver : heartIcon}
                alt="Favoritar"
                className="w-6 h-6"
              />
            </button>
          </div>

          {/* Second Row for Content */}
          <div className="flex w-full flex-grow flex-col">
            <div className="w-full pr-2">
              <div className="flex flex-col justify-between flex-grow">
                {category && (
                  <div className="font-sans text-sm font-bold text-[#EE7911] mb-2">{categoryName}</div>
                )}
                <div className="flex justify-between items-center">
                  <H3 text={entrepreneur.companyName} textColor="purple" />
                  {businessCity && businessState && (
                    <div className="flex items-center mt-2">
                      <img src={distanceIcon} alt="Distance" className="w-4 h-4 mr-1" />
                      <p className="text-sm text-gray-500">{businessCity} - {businessState}</p>
                    </div>
                  )}
                </div>
                <p className="text-base text-gray-600 mt-2 mb-4 break-words">{entrepreneur.aboutBusiness}</p>
                <p className="text-base text-gray-600 mb-2 break-words">Endereço: {businessAddress}</p>
                <p className="text-base text-gray-600 mb-5 break-words">Horários: {businessHours}</p>
                {/* Links */}
                {websiteHref && (
                  <div className="flex items-center mb-3">
                    <img src="/img/ico-url-perfil.svg" alt="" className="w-5 h-5 mr-1.5" />
                    <a href={websiteHref} target="_blank" rel="noopener noreferrer" className="text-[#4663AB] font-sans font-bold text-sm break-all">
                      {websiteLabel}
                    </a>
                  </div>
                )}
                {instagramHref && (
                  <div className="flex items-center mb-3">
                    <img src="/img/ico-instagram-perfil.svg" alt="" className="w-5 h-5 mr-1.5" />
                    <a href={instagramHref} target="_blank" rel="noopener noreferrer" className="text-[#4663AB] font-sans font-bold text-sm">
                      {instagramLabel}
                    </a>
                  </div>
                )}
                {phoneHref && (
                  <div className="flex items-center mb-4">
                    <img src="/img/ico-telefone-perfil.svg" alt="" className="w-5 h-5 mr-1.5" />
                    <a href={`tel:${phoneHref}`} className="text-[#4663AB] font-sans font-bold text-sm">
                      {phoneLabel}
                    </a>
                  </div>
                )}
                {!isOwnEntrepreneur && currentUser && (
                  <div className="mt-2">
                    <Button text="Responder" variant="icon" icon={messageIcon} onClick={handleResponderClick} />
                  </div>
                )}
              </div>
            </div>
            <div className="w-full mt-5">
              {authorInfo && <AuthorDetails authorInfo={authorInfo} title="Perfil do empreendedor" />}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EntrepreneurDetailsPopup;